import { useMemo } from 'react'
import { useServices } from '@/api/queries'
import type { ServiceInfo } from '@/api/types'
import { EmptyState, ErrorState } from '@/components/ui/EmptyState'
import { LoadingBlock } from '@/components/ui/Spinner'
import { MonitoringCard } from './MonitoringCard'
import styles from './MonitoringPage.module.css'
import { ServiceRow } from './ServiceRow'
import { targetLabel, type Target } from './target'

interface ServicesCardProps {
  target: Target
  open: boolean
  onToggle: () => void
}

export function ServicesCard({ target, open, onToggle }: ServicesCardProps) {
  const { data, error, isLoading, isFetching, refetch } = useServices(target, open)
  return (
    <MonitoringCard title={targetLabel(target)} open={open} onToggle={onToggle} onRefresh={() => void refetch()} fetching={isFetching}>
      {(query) => <ServiceList target={target} services={data} loading={isLoading} error={error} query={query} />}
    </MonitoringCard>
  )
}

interface ServiceListProps {
  target: Target
  services: ServiceInfo[] | undefined
  loading: boolean
  error: unknown
  query: string
}

/** Unhealthy services first, then the biggest deployments. */
function ServiceList({ target, services, loading, error, query }: ServiceListProps) {
  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return (services ?? [])
      .filter((service) => !needle || service.name.toLowerCase().includes(needle))
      .sort((a, b) => b.unhealthyInstances - a.unhealthyInstances || b.instanceCount - a.instanceCount || a.name.localeCompare(b.name))
  }, [services, query])

  if (error) return <ErrorState error={error} />
  if (loading || !services) return <LoadingBlock />
  if (rows.length === 0) return <EmptyState>{services.length === 0 ? 'No service' : 'No service matches the filter'}</EmptyState>

  return (
    <ul className={styles.serviceList}>
      {rows.map((service) => (
        <ServiceRow key={service.name} target={target} service={service} />
      ))}
    </ul>
  )
}
